import { activeStatuses, ec, ecId, ecLean, handBack, insertRoll, record, requiredInt, upper, type JanusCtx, type Row } from '../ability/context.js';
import { applyStatusEffect } from '../ability/statuses/apply-status-effect.js';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers.js';
import { rollDiceFaces } from '../ability/rolls/roll-dice-arr.js';
import { rollTypeModifierStacks } from '../ability/skill-roll.js';
import { statusItemId } from '../statuses/expiry.js';
import { processDyingState } from '../port/k4-dispatch/process_dying_state.js';
import { handleDyingProgress, handleRecoveryProgress } from '../port/k4-dispatch/dying_recovery_helpers.js';
import { browserDependencies } from '../port/browser-dependencies.js';
import { nativePortValue } from '../port/native-value.js';
import { plCatalogs } from '../pl/contract.js';
import { composeActualDeath, dropDyingHands } from '../death/compose.js';
import type { E, R } from '../port/k4-dispatch/support-b2.js';
import type { DispatchEnv } from '../port/k4-dispatch/dispatch.js';

const dyingCatalog = plCatalogs();
export const DYING_ROUND_DEFINITION_IDS = [
  ...['dying', 'dying_state', 'dying_state_1', 'dying_state_2', 'recovery_state', 'recovery_state_1', 'recovery_state_2',
    'rotting', 'crippled', 'prone', 'unconscious', 'maimed'].map(name => dyingCatalog.statusEffects.getId(name)),
  dyingCatalog.rollTypes.getId('dying_stratum_roll'),
].filter((id): id is string => !!id);

/** Bridge the existing K4 stratum decision into native tick mutations for the OoC phase selector. */
export function runDyingStratum(ctx: JanusCtx, target: Row): void {
  const catalogs = plCatalogs(), sid = (name: string): string => catalogs.statusEffects.getId(name) ?? handBack('dying:catalog:' + name);
  const has = (id: string): boolean => activeStatuses(ctx, target).some(s => statusItemId(s) === upper(id));
  const dyingId = sid('dying');
  if (!has(dyingId)) return;
  const dying = ctx.read.definition(dyingId) ?? handBack('dying:definition-not-held:' + dyingId);
  const input = { casterId: ecId(target), targetIds: [ecId(target)], abilityId: dyingId, rawDiceMod: 0,
    rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null };
  const remove = (id: string): void => {
    for (const s of [...activeStatuses(ctx, target)].filter(s => statusItemId(s) === upper(id))) removeStoredStatus(ctx, target, s);
  };
  const apply = (id: string, log = true): void => {
    let stage = false;
    for (const family of ['dying_state', 'recovery_state']) if (id === sid(family)) {
      stage = true;
      const old = has(sid(family + '_2')) ? 2 : has(sid(family + '_1')) ? 1 : 0;
      if (old >= 2) handBack('dying:stage-overflow');
      if (old) remove(sid(family + '_' + old));
      id = sid(family + '_' + (old + 1));
    }
    const definition = ctx.read.definition(id); if (!definition) handBack('dying:definition-not-held:' + id);
    applyStatusEffect(ctx, input, definition, id, 'status_effect_applied', target, target,
      { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true, log, 0, false, null, false, stage);
  };
  const clear = (): void => {
    for (const name of ['rotting', 'dying', 'recovery_state_1', 'recovery_state_2', 'dying_state_1', 'dying_state_2']) remove(sid(name));
    if (has(sid('crippled'))) { remove(sid('crippled')); apply(sid('prone')); }
  };
  const log = (type: string): void => insertRoll(ctx, { roll_type_1: type, roll_item_id: sid('dying'), by: target });
  let env: E;
  const deps = {
    'StatusEffect::getId': sid,
    'RollType::getId': (name: string) => catalogs.rollTypes.getId(name) ?? handBack('dying:roll-type:' + name),
    has_status_effect: (_row: unknown, id: string) => has(id),
    layer_value: (row: Row, key: string) => ctx.read.value(row, key),
    query_full_item_arr: (id: string) => ctx.read.definition(id) ?? handBack('dying:definition-not-held:' + id),
    janus_accumulate_roll_type_adv_dis: (adv: R, _item: unknown, _caster: unknown, _target: unknown, rollType: string) => {
      adv.value = rollTypeModifierStacks(ctx, target, [rollType], ['adv', 'dis', 'against_adv', 'against_dis'],
        { usedItemType: 'roll_type', usedRollType: rollType, rollTo: '', opposing: null });
    },
    janus_default_roll_inc: (dice: number, die: number, mod: number, adv: number, result: R, full: R, faces: R) => {
      const roll = rollDiceFaces(ctx, dice, die, adv);
      faces.value = roll.faces; full.value = [[roll.total + Number(mod), 'roll_result'], [Number(mod), 'sum_mod'], ...roll.faces];
      result.value = roll.total + Number(mod);
    },
    insert_roll: (_privacy: unknown, _roll: unknown, type: string, _type2: unknown, _item: unknown, _def: unknown,
      dice: number, die: number, mod: number, adv: number, dc: number, faces: unknown[], result: number) => {
      insertRoll(ctx, { roll_type_1: type, by: target, roll_result: result, rolls_full: faces, dice, die, mod, adv_dis_stacks: adv, dc });
      ctx.rows[ctx.rows.length - 1]!.roll_private = 0;
    },
    remove_recovery_and_dying_states: clear,
    adjust_v_current: (_row: unknown, delta: number) => Math.min(requiredInt(record(record(target.computed)?.vitality)?.effective_v_max ?? ecLean(target, 'v_max'), 'dying:v-max'), Number(ecLean(target, 'v_current') ?? 0) + delta),
    update_encounter_creature_stats: (_row: unknown, changes: Row) => Object.assign(ec(target), changes),
    log_recovery: () => log('recovers'), log_stabilization: () => log('stabilized'), log_consciousness: () => log('is_conscious'),
    remove_status_effect_item_id_single_connection: (_row: unknown, _affected: unknown, id: string) => remove(id),
    apply_status_effect: (_connection: unknown, _caster: unknown, _target: unknown, _targets: unknown, _affected: unknown,
      _origin: unknown, id: string, _ends: unknown, _roll: unknown, logged = true) => apply(id, logged),
    handle_recovery_progress: (recovery: unknown, dying: unknown, stratum: unknown, row: R, affected: R, roll: unknown) =>
      handleRecoveryProgress(recovery, dying, stratum, row, affected, roll, env as unknown as DispatchEnv),
    handle_dying_progress: (row: R, affected: R, result: unknown, natural: unknown, dying: unknown, stratum: unknown, roll: unknown) =>
      handleDyingProgress(row, affected, result, natural, dying, stratum, roll, env as unknown as DispatchEnv),
    ft_campaign_context: () => null,
    campaign_arr_has_active_house_rule_type: () => ctx.read.houseRules?.()?.maimedOnDyingStratumFailBy5 === true,
    apply_maimed_status: () => apply(sid('maimed')),
    drop_target_equipped_in_hand_inventory: () => dropDyingHands(ctx, target),
    kill_creature: () => composeActualDeath(ctx, target, target, dying, 0),
    insert_log_message: (_roll: unknown, type: string, item: unknown) => insertRoll(ctx, { roll_type_1: type, roll_item_id: upper(item) || null, by: target }),
  };
  env = browserDependencies(new Proxy(deps, { get: (obj, key) => Reflect.get(obj, key) ?? (() => handBack('dying:dependency:' + String(key))) })) as unknown as E;
  nativePortValue(processDyingState({ value: target }, { value: [] }, '', env));
}
